/**
 * Pure formatting/derivation logic for the "Positions & sizing" panel (R3 —
 * observatory visibility of smoother state), factored out of `SizingPanel.ts`
 * so the schema-parsing/decision-wording/bar-math rules are unit-testable
 * without touching the DOM (mirrors `marketView.ts`'s established split).
 */

import type { SizingDocument, SizingSlot } from "../types/incremental";
import type { RoleSnapshot } from "../data/snapshotIndex";
import { REASON_LABELS } from "../brain/sessionStatus";
import { cappedPercent, formatPercent, formatPrice, formatQuantity } from "../format/number";

export type SizingPanelState =
  | { readonly kind: "empty" }
  | {
      readonly kind: "ready";
      readonly asOfSeq: number;
      readonly capWeight: number;
      readonly grossCapWeight: number;
      readonly grossCurrentWeight: number | null;
      /** `true` when the caps/band/cooldown figures came from this
       * module's own default constants, never the live loop's actual
       * reported config (`SizingDocument.config_source`) — the panel shows
       * a small "settings not yet reported" note in this case. */
      readonly configAssumed: boolean;
      /** OQA: the risk policy of the latest sized decision ("core_tilt"/"graded")
       * and the core's build-in ramp (0..1), or `null` before one. */
      readonly policy: string | null;
      readonly buildRamp: number | null;
      readonly slots: readonly SizingSlot[];
    };

/** A `null` document (the ref absent — an unmigrated role, or a v2 role
 * whose publisher hasn't folded a `sizing` object yet) and a document with
 * zero slots (the honest shape a role publishes before its first
 * `sizing.decision` event) are both the same user-facing state: nothing to
 * show yet, never a fabricated row. */
export function buildSizingPanelState(document: SizingDocument | null): SizingPanelState {
  if (!document || document.slots.length === 0) return { kind: "empty" };
  return {
    kind: "ready",
    asOfSeq: document.as_of_seq,
    capWeight: document.cap_weight,
    grossCapWeight: document.gross_cap_weight,
    grossCurrentWeight: document.gross_current_weight,
    configAssumed: document.config_source === "assumed-defaults",
    policy: document.policy ?? null,
    buildRamp: decimalOrNull(document.build_ramp),
    slots: document.slots,
  };
}

function decimalOrNull(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

/** OQA: the book-level policy line — "Core + tilt · core build-in 15.0%" — or
 * `null` before any sized decision. The ramp is the share of the 60% equal-weight
 * core phased in so far (the core builds in over ~1 day of decision time). */
export function policyText(policy: string | null, buildRamp: number | null): string | null {
  if (policy === null) return null;
  if (policy === "core_tilt") {
    const ramp = buildRamp !== null ? ` · core build-in ${formatPercent(buildRamp, 1)}` : "";
    return `Core + tilt${ramp}`;
  }
  if (policy === "graded") return "Graded (signal-scaled) sizing";
  return policy.replace(/_/g, " ");
}

/** OQA: one slot's core_tilt allocation in words — "core 6.00% × (1 + tilt
 * -0.000) × build-in 15.0% → target 0.90%" — from the decision's own `risk`
 * block (verbatim strings, nothing recomputed beyond formatting). `null` when
 * the slot carries no core_tilt risk block. */
export function coreTiltText(slot: SizingSlot): string | null {
  const risk = slot.risk;
  if (!risk || risk.policy !== "core_tilt") return null;
  const base = decimalOrNull(risk.base_weight);
  const tilt = decimalOrNull(risk.tilt);
  const target = decimalOrNull(risk.target_weight);
  const ramp = decimalOrNull(risk.build_ramp);
  if (base === null || target === null) return null;
  const parts = [`core ${formatPercent(base)}`];
  if (tilt !== null) {
    const tiltText =
      Math.abs(tilt) < 5e-7 ? "0" : `${tilt > 0 ? "+" : "−"}${Math.abs(tilt).toFixed(3)}`;
    parts.push(`tilt ${tiltText}`);
  }
  if (ramp !== null) parts.push(`build-in ${formatPercent(ramp, 1)}`);
  return `${parts.join(" · ")} → target ${formatPercent(target)}`;
}

/** `weight / cap` as a 0-100 clamped percent, for a horizontal bar fill —
 * delegates to the one shared `cappedPercent` (code review finding: this and
 * the fleet screen's drawdown bar each had their own copy of this exact
 * clamp-and-scale math). Kept as its own named export since every call site
 * here reads more clearly as "weight against its cap" than the generic name. */
export function weightBarPercent(weight: number | null, cap: number): number {
  return cappedPercent(weight, cap);
}

/** `true` when a symbol's marked weight has reached (or slightly exceeds,
 * floating-point noise aside) its own per-symbol cap — used to tone the bar
 * as "at cap" rather than merely "far along." */
export function isAtCap(weight: number | null, cap: number): boolean {
  if (weight === null || !Number.isFinite(weight) || cap <= 0) return false;
  return weight >= cap - 1e-9;
}

/** Sizing-specific reasons `decision.suppressed` carries that
 * `brain/sessionStatus.ts`'s `REASON_LABELS` doesn't (that vocabulary is
 * `runtime.compute_request_rejected`'s own, a different event — see
 * `REASON_LABELS`'s doc comment) — merged with, never overriding, the shared
 * labels below so the two S28 warm-up codes the vocabularies share
 * (`insufficient_samples`/`prior_session_samples`) read with exactly one
 * wording app-wide (code review finding). */
const SIZING_ONLY_REASON_LABELS: Readonly<Record<string, string>> = {
  extended_hours_not_armed: "extended hours not armed",
  session_not_tradable: "session not tradable",
  no_mark: "no mark yet",
};

function humanizeReason(reason: string): string {
  const known = REASON_LABELS[reason] ?? SIZING_ONLY_REASON_LABELS[reason];
  if (known) return known;
  return reason.replace(/_/g, " ");
}

function detailString(detail: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = detail[key];
  return typeof value === "string" || typeof value === "number" ? String(value) : null;
}

/** The last decision in plain words (owner examples: "Holding — change
 * under 1% band", "Cooling down 3 more wakes", "Bought 3 QQQ") — every
 * branch reads only the `decision_detail` keys that decision kind actually
 * carries (`docs/OBSERVATORY_TELEMETRY.md` "Sizing view (R3)"), never
 * guessing at a missing one. */
export function decisionText(slot: SizingSlot): string {
  const detail = slot.decision_detail;
  switch (slot.decision) {
    case "traded": {
      const side = detailString(detail, "side");
      const quantity = detailString(detail, "quantity");
      const verb = side === "sell" ? "Sold" : side === "buy" ? "Bought" : "Traded";
      return quantity
        ? `${verb} ${formatQuantity(quantity)} ${slot.symbol}`
        : `${verb} ${slot.symbol}`;
    }
    case "held_in_band":
      return "Holding — change under the rebalance band";
    case "cooldown_blocked": {
      const remaining = slot.cooldown_wakes_remaining;
      return remaining !== null && remaining > 0
        ? `Cooling down — ${remaining} more wake${remaining === 1 ? "" : "s"}`
        : "Cooling down";
    }
    case "rejected": {
      const side = detailString(detail, "side");
      const reason = detailString(detail, "reason");
      const verb = side === "sell" ? "Sell" : side === "buy" ? "Buy" : "Trade";
      return reason ? `${verb} rejected — ${humanizeReason(reason)}` : `${verb} rejected`;
    }
    case "gapped":
      return "Gap detected — no decision this wake";
    case "no_mark":
      return "No mark yet";
    case "suppressed": {
      const reason = detailString(detail, "reason");
      return reason ? `Holding — ${humanizeReason(reason)}` : "Holding — decision suppressed";
    }
    default:
      return "No sizing decision recorded yet";
  }
}

/** A short cooldown chip's text, or `null` when there is nothing to show
 * (no cooldown active) — `SizingPanel.ts` only renders the chip when this is
 * non-null, so an idle symbol never shows a stray "0 more wakes" chip. */
export function cooldownChipText(slot: SizingSlot): string | null {
  const remaining = slot.cooldown_wakes_remaining;
  if (remaining === null || remaining <= 0) return null;
  return `Cooling down · ${remaining} more wake${remaining === 1 ? "" : "s"}`;
}

export interface DualSparkGeometry {
  readonly rawPath: string;
  readonly smoothedPath: string;
  readonly width: number;
  readonly height: number;
}

/** Raw-vs-smoothed spark geometry, both series scaled to the SAME shared
 * domain (unlike `charts/sparkline.ts`'s `buildSparkline`, which scales one
 * series to its own min/max) — the whole point is comparing the two lines
 * at a glance, which a per-series-normalized pair would misrepresent
 * (e.g. a perfectly flat smoothed line would still fill the chart's full
 * height on its own scale). Returns `null` (never a guessed/flat pair of
 * lines) when either series has fewer than two finite points. */
export function buildDualSpark(
  raw: readonly number[],
  smoothed: readonly number[],
  width: number,
  height: number,
  padding = 2,
): DualSparkGeometry | null {
  const rawFinite = raw.filter((v) => Number.isFinite(v));
  const smoothedFinite = smoothed.filter((v) => Number.isFinite(v));
  if (rawFinite.length < 2 || smoothedFinite.length < 2) return null;

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const v of rawFinite) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  for (const v of smoothedFinite) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const span = max - min;
  const innerHeight = Math.max(1, height - padding * 2);

  const toPath = (series: readonly number[]): string => {
    const stepX = series.length > 1 ? (width - padding * 2) / (series.length - 1) : 0;
    return series
      .map((v, i) => {
        const x = padding + stepX * i;
        const fraction = span === 0 ? 0.5 : (v - min) / span;
        const y = padding + (1 - fraction) * innerHeight;
        return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  };

  return {
    rawPath: toPath(rawFinite),
    smoothedPath: toPath(smoothedFinite),
    width,
    height,
  };
}

/** `"50.0000 @ $99.07"`-style position text, or `"flat"` for a known-zero
 * quantity, or `"–"` when the quantity itself is unknown (never fabricated
 * — mirrors `portfolioPositions.ts`'s "-" convention for a missing field).
 * Formatted exactly like `PortfolioPanel.ts`'s own positions table
 * (`formatDecimal(quantity, 4)`/`formatMoney(mark)` — code review finding:
 * a second, independent `${quantity}`/`` $${mark.toFixed(2)}` `` copy here
 * had already drifted on both the quantity's fractional-share precision and
 * the money formatting's thousands separator). */
export function positionText(slot: SizingSlot): string {
  const quantity = Number.parseFloat(slot.position_quantity ?? "");
  if (!Number.isFinite(quantity)) return "–";
  if (quantity === 0) return "flat";
  // OQA: verbatim quantity and a magnitude-aware mark (crypto 1e-9 grid).
  const markText =
    slot.mark_price !== null ? ` @ ${formatPrice(slot.mark_price, { currency: true })}` : "";
  return `${formatQuantity(slot.position_quantity)}${markText}`;
}

/** The symbol's own marked notional (`quantity * mark`), or `null` when
 * either leg is unknown — `0` for a known-flat position, never fabricated
 * from a partial read. */
export function markedValue(slot: SizingSlot): number | null {
  const quantity = Number.parseFloat(slot.position_quantity ?? "");
  if (!Number.isFinite(quantity)) return null;
  if (quantity === 0) return 0;
  const mark = slot.mark_price !== null ? Number.parseFloat(slot.mark_price) : null;
  if (mark === null || !Number.isFinite(mark)) return null;
  return quantity * mark;
}

/** Fetches `role.sizing()`, isolating a genuine fetch/parse failure to a
 * `null` result — never letting it reject a `Promise.all` that also awaits
 * OTHER, more critical data (NAV, positions, last decision) for the same
 * screen (code review finding: `VitalsHero`/`FleetScreen` used to fetch
 * sizing in the SAME `Promise.all` as those, so a sizing-only failure blanked
 * everything, not just the sizing/gross-exposure fields). `role.sizing()`
 * itself already treats "not published yet" as `null`; this additionally
 * guards against a genuine thrown error (network/parse) doing the same. */
export async function fetchSizingSafely(
  role: Pick<RoleSnapshot, "sizing">,
): Promise<SizingDocument | null> {
  try {
    return await role.sizing();
  } catch (error) {
    console.warn("observatory: sizing fetch failed; continuing without it", error);
    return null;
  }
}

/** A stable fingerprint of everything in `doc` that actually changes what
 * the sizing panel/hero/fleet-card render — used as a poller's "did anything
 * meaningful change" key instead of the document's own `as_of_seq` (code
 * review finding: `as_of_seq` advances on EVERY publish, including ones with
 * no sizing-relevant change at all, which re-rendered this panel's rows for
 * no visible reason). `null` document -> `"none"`. */
export function sizingFingerprint(doc: SizingDocument | null): string {
  if (!doc) return "none";
  const parts = doc.slots.map((slot) =>
    [
      slot.symbol,
      slot.decision,
      slot.decision_tick,
      slot.raw_signal,
      slot.smoothed_signal,
      slot.target_weight,
      slot.current_weight,
      slot.cooldown_wakes_remaining,
      slot.position_quantity,
      slot.mark_price,
      slot.risk?.target_weight,
      slot.risk?.tilt,
      slot.risk?.build_ramp,
    ].join(":"),
  );
  return [
    String(doc.gross_current_weight),
    doc.config_source,
    doc.policy ?? "",
    doc.build_ramp ?? "",
    ...parts,
  ].join("|");
}
